/**
 * Same-origin fetch with credentials. On session 401 → hard redirect to /auth.
 */
const SESSION_ERRORS = new Set([
  'missing_token',
  'session_expired',
  'session_claims_mismatch',
]);

let redirectingToAuth = false;

export function redirectToAuth() {
  if (redirectingToAuth) return;
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '';
  if (path === '/auth' || path.startsWith('/auth/')) return;
  redirectingToAuth = true;
  try {
    localStorage.removeItem('id');
  } catch {
    /* ignore */
  }
  window.location.replace('/auth');
}

export function isSessionErrorBody(body) {
  return Boolean(
    body &&
      typeof body === 'object' &&
      body.success === false &&
      typeof body.error === 'string' &&
      SESSION_ERRORS.has(body.error)
  );
}

export function apiErrorMessage(body, status) {
  if (body && typeof body === 'object') {
    if (typeof body.description === 'string' && body.description.trim()) {
      return body.description.trim();
    }
    if (typeof body.show_text === 'string' && body.show_text.trim()) {
      return body.show_text.trim();
    }
    if (typeof body.error === 'string' && body.error.trim()) {
      return body.error.trim();
    }
  }
  return status ? `HTTP ${status}` : 'Ошибка запроса';
}

/**
 * Parse JSON API response. Lists may be raw arrays (success implied).
 * Envelopes with success:false are never ok.
 */
export async function readApiJson(response, { expectArray = false } = {}) {
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const message = apiErrorMessage(body, response.status);

  if (!response.ok) {
    return { ok: false, body, message, status: response.status };
  }

  if (body && typeof body === 'object' && !Array.isArray(body) && body.success === false) {
    return { ok: false, body, message, status: response.status };
  }

  if (expectArray && !Array.isArray(body)) {
    return {
      ok: false,
      body,
      message: message || 'Некорректный ответ сервера',
      status: response.status,
    };
  }

  return { ok: true, body, message: '', status: response.status };
}

export async function apiFetch(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
  });

  if (response.status === 401) {
    let body = null;
    try {
      const ct = response.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        body = await response.clone().json();
      }
    } catch {
      /* ignore */
    }
    if (!body || isSessionErrorBody(body) || body?.error === 'missing_token') {
      redirectToAuth();
    }
  }

  return response;
}
